import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Loader2, Mail, CheckCircle2, XCircle } from "lucide-react";

type State =
  | { status: "loading" }
  | { status: "valid" }
  | { status: "already" }
  | { status: "done" }
  | { status: "error"; message: string };

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState<State>({ status: "loading" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ status: "error", message: "Falta el token de cancelación." });
      return;
    }
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/handle-email-unsubscribe?token=${encodeURIComponent(token)}`;
    fetch(url, {
      headers: { apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.valid) setState({ status: "valid" });
        else if (data.reason === "already_unsubscribed")
          setState({ status: "already" });
        else
          setState({
            status: "error",
            message: data.error || "Token inválido o expirado.",
          });
      })
      .catch(() =>
        setState({ status: "error", message: "Error de conexión." }),
      );
  }, [token]);

  const confirm = async () => {
    if (!token) return;
    setSubmitting(true);
    const { data, error } = await supabase.functions.invoke(
      "handle-email-unsubscribe",
      { body: { token } },
    );
    setSubmitting(false);
    if (error) {
      setState({ status: "error", message: error.message });
    } else if (data?.success) {
      setState({ status: "done" });
    } else if (data?.reason === "already_unsubscribed") {
      setState({ status: "already" });
    } else {
      setState({ status: "error", message: "No fue posible procesar." });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-lg border bg-card p-8 shadow-sm text-center">
        <Mail className="mx-auto mb-4 h-10 w-10 text-muted-foreground" />
        <h1 className="text-xl font-semibold mb-2">Cancelar suscripción</h1>

        {state.status === "loading" && (
          <div className="flex items-center justify-center py-6 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        )}

        {state.status === "valid" && (
          <>
            <p className="text-sm text-muted-foreground mb-6">
              ¿Quieres dejar de recibir correos de Smart Canvas a esta
              dirección?
            </p>
            <Button onClick={confirm} disabled={submitting} className="w-full">
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Confirmar cancelación"
              )}
            </Button>
          </>
        )}

        {state.status === "done" && (
          <div className="text-sm text-muted-foreground flex flex-col items-center gap-2">
            <CheckCircle2 className="h-8 w-8 text-foreground" />
            <p>Listo. Ya no recibirás más correos a esta dirección.</p>
          </div>
        )}

        {state.status === "already" && (
          <div className="text-sm text-muted-foreground flex flex-col items-center gap-2">
            <CheckCircle2 className="h-8 w-8 text-foreground" />
            <p>Esta dirección ya estaba dada de baja.</p>
          </div>
        )}

        {state.status === "error" && (
          <div className="text-sm text-muted-foreground flex flex-col items-center gap-2">
            <XCircle className="h-8 w-8 text-destructive" />
            <p>{state.message}</p>
          </div>
        )}
      </div>
    </div>
  );
}